/**
 * The discussions writer (design 10b, `/community/discussions/new`): the draft, every rule the
 * editor and its checklist apply, and the mapping to DealHunt's create body. Pure — the
 * components in `components/community/discussions/writer/` only render what this answers and
 * dispatch `writerReducer` actions. Contract: `dealhunt/docs/sg-owner-discussions-contract.md`
 * §2 (blocks and limits, mirrored here so the editor stops where DealHunt would refuse) and §4.
 *
 * Decided in the contract (2026-10-04), so not built here: no server drafts (the draft lives in
 * this browser's `localStorage`, `DRAFT_STORAGE_KEY`) and no Open Graph fetch (a link's title is
 * derived from its URL path, as the design mock does).
 */
import { AUDIO_NODE_SLUG } from 'lib/dealhunt/constants'
import { brandedTitle, categoryLeafLabel } from 'lib/dealhunt/url'
import { OWNER_PHOTO_MAX_BYTES, type DealHuntSuggestHit } from 'lib/dealhunt/types'
import type {
  DiscussionBlock,
  DiscussionCreateBody,
  DiscussionGearInput,
  DiscussionImage,
  DiscussionImageStatus,
  DiscussionKind,
  DiscussionPatchBody,
  DiscussionThread,
} from './discussions-types'
import type { CommunityCategoryNode, CommunityProduct } from './types'
import { categoryChildren, findCategory, hitNodeId, twoDigits } from './model'
import { DISCUSSIONS_PATH, discussionsHref, imageSource, imageStatusMarker } from './discussions'
import { LINK_LIMITS, linkPreview, type LinkPreview } from './link-preview'

/**
 * DealHunt's limits — every number is DealHunt's `DISCUSSION_LIMITS`
 * (`shared/ownerCommunityTypes.ts`), quoted from the contract's §2 "Every writer-visible limit, as
 * built" table; SoundGuys sets none of its own. The editor stops at them; DealHunt still validates.
 * DealHunt counts Unicode code points, the editor UTF-16 units (`.length`): never more than
 * DealHunt's count, so the editor can only stop early on astral characters, never let one past.
 */
export const DISCUSSION_LIMITS = {
  /** "title | 3–150, one line" (`title_max`) */
  titleChars: 150,
  /** "blocks | ≤ 30" (`blocks_max`) */
  blocks: 30,
  /** "text block | ≤ 5 000" (`text_block_max`) */
  textChars: 5000,
  /** "total text | ≤ 20 000 = text bodies + headings + captions" (`text_total_max`) */
  totalTextChars: 20000,
  /** "heading | ≤ 120, one line" (`heading_max`) */
  headingChars: 120,
  /** "caption / image alt | ≤ 200 each" (`caption_max`) */
  captionChars: 200,
  /** "link block | … title ≤ 120" (`link_title_max`) */
  linkTitleChars: LINK_LIMITS.linkTitleChars,
  /** "link block | `https://` only, url ≤ 2 000 chars" (`url_max`) */
  urlChars: LINK_LIMITS.urlChars,
  /** "images | ≤ 12 per post, 1–6 per set" (`images_per_set`, `images_per_post`) */
  imagesPerSet: 6,
  imagesPerPost: 12,
  /** "linked gear | ≤ 5 items (`gear_max`)" */
  gear: 5,
  /** §4 "Media: `multer` memory ≤ 4 MB in" */
  imageBytes: OWNER_PHOTO_MAX_BYTES,
} as const

/** The title length that unlocks posting (design: `canPost = title.length > 8`). */
export const TITLE_MIN_TO_POST = 9

// ── the draft ────────────────────────────────────────────────────────────────

/**
 * One tile of a photo set. `queued` waits for a session (or a free upload slot), `uploading`
 * carries `progress` 0–1, `done` holds DealHunt's media, `error` holds the reader-safe line.
 * Only `done` tiles survive a reload: a local file cannot be stored.
 */
export interface DraftImage {
  key: string
  status: 'queued' | 'uploading' | 'done' | 'error'
  progress: number
  media_id: string | null
  url: string | null
  width: number
  height: number
  error: string | null
  /** When the upload finished (epoch ms) — a stored draft drops photos DealHunt has collected. */
  uploaded_at: number | null
  /** A quiet line on the tile — editing only: "Not approved" on a report-hidden photo
   *  (`tileFromPosted`). Absent / null: none. */
  pending_line?: string | null
  /** Editing a posted discussion only (`draftFromDiscussion`): the photo's state on the post as
   *  its author sees it (`/mine`). A `hidden` photo stays on the post if kept. Absent on a new
   *  post's tiles. */
  posted_status?: DiscussionImageStatus
  /** Editing only: a smaller stored size for the tile (DealHunt's `card` variant), so a phone
   *  does not draw twelve 2048 px originals. The tile falls back to `url`. */
  preview_url?: string | null
}

/**
 * DealHunt deletes media not attached to a post after 24 h (contract §1, GC): a stored draft keeps
 * an uploaded photo for a little less, so a draft reopened later never posts a deleted media id.
 */
export const PHOTO_DRAFT_TTL_MS = 23 * 60 * 60 * 1000

/** A photo whose upload is older than `PHOTO_DRAFT_TTL_MS`: DealHunt may have collected it. */
export interface ExpiredPhoto {
  blockId: number
  key: string
  /** 1-based position in its set, for the line that names it. */
  position: number
}

/**
 * Uploaded photos past the 23 h window, checked at POST time — a tab left open past a day never
 * sends a media id DealHunt has collected (it would answer `media_invalid` for the whole post).
 * `parseDraft` checks the same window only when a stored draft loads.
 */
export function expiredPhotos(draft: WriterDraft, now: number): ExpiredPhoto[] {
  const out: ExpiredPhoto[] = []
  for (const b of draft.blocks) {
    if (b.type !== 'images') {
      continue
    }
    b.images.forEach((im, i) => {
      if (
        im.status === 'done' &&
        im.uploaded_at !== null &&
        now - im.uploaded_at > PHOTO_DRAFT_TTL_MS
      ) {
        out.push({ blockId: b.id, key: im.key, position: i + 1 })
      }
    })
  }
  return out
}

/** The set's line after its expired photos were removed: which ones, and what to do. */
export function expiredPhotosLine(positions: readonly number[]): string {
  const n = positions.length
  const which = n === 1 ? `Photo ${positions[0]}` : `Photos ${positions.join(', ')}`
  return `${which} removed: uploaded over 23 hours ago and no longer kept. Add ${
    n === 1 ? 'it' : 'them'
  } again.`
}

export type DraftBlock =
  | { id: number; type: 'text'; body: string }
  | { id: number; type: 'heading'; text: string }
  | { id: number; type: 'images'; images: DraftImage[]; caption: string }
  | { id: number; type: 'link'; url: string }

export type DraftBlockType = DraftBlock['type']

/** A linked product or brand as the chip shows it; `full_slug` places a product in a category. */
export type DraftGear =
  | {
      kind: 'product'
      node_id: string
      name: string
      sub: string
      image_url: string | null
      full_slug: string | null
    }
  | { kind: 'brand'; name: string; slug: string | null; sub: string }

export interface WriterDraft {
  kind: DiscussionKind
  title: string
  blocks: DraftBlock[]
  gear: DraftGear[]
  categoryId: string | null
  /** The reader picked a pill: a later product link no longer moves the category. */
  categoryTouched: boolean
  nextId: number
  /** The `?kind=&product=` preset already applied to this draft (`applyPreset`). */
  presetKey: string | null
  /** When this browser stored the draft (epoch ms) — only on a draft read back by `parseDraft`
   *  (absent from older stored drafts); `serializeDraft` writes the save's own time. */
  savedAt?: number
}

export function emptyDraft(kind: DiscussionKind = 'question'): WriterDraft {
  return {
    kind,
    title: '',
    blocks: [{ id: 1, type: 'text', body: '' }],
    gear: [],
    categoryId: null,
    categoryTouched: false,
    nextId: 2,
    presetKey: null,
  }
}

export function gearKey(g: DraftGear): string {
  return g.kind === 'product' ? `p:${g.node_id}` : `b:${g.name.toLowerCase()}`
}

/** One-line title: line breaks become spaces, clamped to DealHunt's 150. */
export function cleanTitle(raw: string): string {
  return raw.replace(/[\r\n]+/g, ' ').slice(0, DISCUSSION_LIMITS.titleChars)
}

function imageCount(blocks: DraftBlock[]): number {
  return blocks.reduce((n, b) => n + (b.type === 'images' ? b.images.length : 0), 0)
}

/** How many more photos the set `blockId` can take: ≤ 6 per set and ≤ 12 per post. */
export function imageRoom(draft: WriterDraft, blockId: number): number {
  const block = draft.blocks.find(b => b.id === blockId)
  if (!block || block.type !== 'images') {
    return 0
  }
  return Math.max(
    0,
    Math.min(
      DISCUSSION_LIMITS.imagesPerSet - block.images.length,
      DISCUSSION_LIMITS.imagesPerPost - imageCount(draft.blocks)
    )
  )
}

export function canAddBlock(draft: WriterDraft): boolean {
  return draft.blocks.length < DISCUSSION_LIMITS.blocks
}

/** The characters a block adds to DealHunt's 20 000 total: a text body, a heading, a caption. */
function blockChars(b: DraftBlock): number {
  switch (b.type) {
    case 'text':
      return b.body.length
    case 'heading':
      return b.text.length
    case 'images':
      return b.caption.length
    default:
      return 0
  }
}

/** Characters the post already holds toward the 20 000 total (text + headings + captions, as
 *  DealHunt counts it — contract §2). */
export function postChars(blocks: readonly DraftBlock[]): number {
  return blocks.reduce((n, b) => n + blockChars(b), 0)
}

function ownCap(b: DraftBlock | undefined): number {
  switch (b?.type) {
    case 'text':
      return DISCUSSION_LIMITS.textChars
    case 'heading':
      return DISCUSSION_LIMITS.headingChars
    case 'images':
      return DISCUSSION_LIMITS.captionChars
    default:
      return 0
  }
}

/** The longest value block `blockId`'s text (body, heading or caption) may hold: its own cap and
 *  what the post's 20 000 total leaves. */
export function textRoom(draft: WriterDraft, blockId: number): number {
  const block = draft.blocks.find(b => b.id === blockId)
  const own = block ? blockChars(block) : 0
  const left = DISCUSSION_LIMITS.totalTextChars - (postChars(draft.blocks) - own)
  return Math.max(0, Math.min(ownCap(block), left))
}

/** Room for one more linked product or brand (≤ 5, `gear_max`). */
export function canAddGear(draft: Pick<WriterDraft, 'gear'>): boolean {
  return draft.gear.length < DISCUSSION_LIMITS.gear
}

export type WriterAction =
  | { type: 'load'; draft: WriterDraft }
  | { type: 'setKind'; kind: DiscussionKind }
  | { type: 'setTitle'; title: string }
  | { type: 'addBlock'; block: DraftBlockType }
  | { type: 'moveBlock'; id: number; delta: -1 | 1 }
  /** The phone's reorder mode: a drag drops block `id` at position `index` (clamped). */
  | { type: 'moveBlockTo'; id: number; index: number }
  | { type: 'removeBlock'; id: number }
  | { type: 'setText'; id: number; value: string }
  | { type: 'setCaption'; id: number; value: string }
  | { type: 'setUrl'; id: number; value: string }
  | { type: 'addImages'; id: number; images: DraftImage[] }
  | { type: 'updateImage'; id: number; key: string; patch: Partial<DraftImage> }
  | { type: 'removeImage'; id: number; key: string }
  | { type: 'addGear'; gear: DraftGear; categoryId?: string | null }
  | { type: 'removeGear'; key: string }
  | { type: 'setCategory'; id: string | null }

function newBlock(id: number, type: DraftBlockType): DraftBlock {
  switch (type) {
    case 'text':
      return { id, type, body: '' }
    case 'heading':
      return { id, type, text: '' }
    case 'images':
      return { id, type, images: [], caption: '' }
    case 'link':
      return { id, type, url: '' }
  }
}

function mapBlock(draft: WriterDraft, id: number, fn: (b: DraftBlock) => DraftBlock): WriterDraft {
  let changed = false
  const blocks = draft.blocks.map(b => {
    if (b.id !== id) {
      return b
    }
    const next = fn(b)
    changed = changed || next !== b
    return next
  })
  return changed ? { ...draft, blocks } : draft
}

type ImagesBlock = Extract<DraftBlock, { type: 'images' }>

function mapImages(
  draft: WriterDraft,
  id: number,
  fn: (b: ImagesBlock) => ImagesBlock
): WriterDraft {
  return mapBlock(draft, id, b => (b.type === 'images' ? fn(b) : b))
}

/** `items` with the one at `from` moved to `to` (clamped into range); the same array when nothing
 *  moves or `from` is out of range. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= items.length) {
    return items as T[]
  }
  const target = Math.max(0, Math.min(items.length - 1, Math.trunc(to)))
  if (target === from || Number.isNaN(target)) {
    return items as T[]
  }
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(target, 0, moved)
  return next
}

export function writerReducer(draft: WriterDraft, action: WriterAction): WriterDraft {
  switch (action.type) {
    case 'load':
      return action.draft
    case 'setKind':
      return draft.kind === action.kind ? draft : { ...draft, kind: action.kind }
    case 'setTitle':
      return { ...draft, title: cleanTitle(action.title) }
    case 'addBlock':
      if (!canAddBlock(draft)) {
        return draft
      }
      return {
        ...draft,
        blocks: [...draft.blocks, newBlock(draft.nextId, action.block)],
        nextId: draft.nextId + 1,
      }
    case 'moveBlock': {
      const i = draft.blocks.findIndex(b => b.id === action.id)
      const j = i + action.delta
      if (i < 0 || j < 0 || j >= draft.blocks.length) {
        return draft
      }
      const blocks = [...draft.blocks]
      ;[blocks[i], blocks[j]] = [blocks[j], blocks[i]]
      return { ...draft, blocks }
    }
    case 'moveBlockTo': {
      const blocks = moveItem(
        draft.blocks,
        draft.blocks.findIndex(b => b.id === action.id),
        action.index
      )
      return blocks === draft.blocks ? draft : { ...draft, blocks }
    }
    case 'removeBlock':
      return { ...draft, blocks: draft.blocks.filter(b => b.id !== action.id) }
    case 'setText':
      return mapBlock(draft, action.id, b => {
        if (b.type === 'text') {
          return { ...b, body: action.value.slice(0, textRoom(draft, b.id)) }
        }
        if (b.type === 'heading') {
          return {
            ...b,
            text: action.value.replace(/[\r\n]+/g, ' ').slice(0, textRoom(draft, b.id)),
          }
        }
        return b
      })
    case 'setCaption':
      return mapImages(draft, action.id, b => ({
        ...b,
        caption: action.value.slice(0, textRoom(draft, b.id)),
      }))
    case 'setUrl': {
      const url = action.value.trim().slice(0, DISCUSSION_LIMITS.urlChars)
      return mapBlock(draft, action.id, b => (b.type === 'link' ? { ...b, url } : b))
    }
    case 'addImages': {
      const room = imageRoom(draft, action.id)
      if (!room || !action.images.length) {
        return draft
      }
      return mapImages(draft, action.id, b => ({
        ...b,
        images: [...b.images, ...action.images.slice(0, room)],
      }))
    }
    case 'updateImage':
      return mapImages(draft, action.id, b => ({
        ...b,
        images: b.images.map(im => (im.key === action.key ? { ...im, ...action.patch } : im)),
      }))
    case 'removeImage':
      return mapImages(draft, action.id, b => ({
        ...b,
        images: b.images.filter(im => im.key !== action.key),
      }))
    case 'addGear': {
      const key = gearKey(action.gear)
      if (!canAddGear(draft) || draft.gear.some(g => gearKey(g) === key)) {
        return draft
      }
      const categoryId =
        !draft.categoryTouched && !draft.categoryId && action.categoryId
          ? action.categoryId
          : draft.categoryId
      return { ...draft, gear: [...draft.gear, action.gear], categoryId }
    }
    case 'removeGear':
      return { ...draft, gear: draft.gear.filter(g => gearKey(g) !== action.key) }
    case 'setCategory':
      return { ...draft, categoryId: action.id, categoryTouched: true }
  }
  return draft
}

/** The cover: the first photo of the first photo set (contract §2). */
export function isCover(draft: WriterDraft, blockId: number, index: number): boolean {
  const first = draft.blocks.find(b => b.type === 'images' && b.images.length > 0)
  return index === 0 && first?.id === blockId
}

/** A photo is still on its way: posting now would drop it. */
export function uploadsPending(draft: WriterDraft): boolean {
  return draft.blocks.some(
    b =>
      b.type === 'images' &&
      b.images.some(im => im.status === 'queued' || im.status === 'uploading')
  )
}

/** Textarea height for a text block, as the design sizes it: ~78 characters a row, ≥ 3 rows. */
export function textRows(body: string): number {
  return Math.max(3, Math.ceil(body.length / 78) + (body.match(/\n/g) ?? []).length)
}

// ── files ────────────────────────────────────────────────────────────────────

/** DealHunt re-encodes to webp and refuses SVG, GIF and anything that is not an image. */
const REFUSED_TYPES = new Set(['image/svg+xml', 'image/gif'])

/** Why this file cannot be a photo, or null when it can. */
export function fileRefusal(file: { type: string; size: number }): 'type' | 'size' | null {
  if (!file.type.startsWith('image/') || REFUSED_TYPES.has(file.type)) {
    return 'type'
  }
  return file.size > DISCUSSION_LIMITS.imageBytes ? 'size' : null
}

/** The line under a photo set after a pick or a drop left files out. */
export function skippedFilesLine(skipped: { type: number; size: number; room: number }): string {
  const parts: string[] = []
  if (skipped.type) {
    parts.push(`${skipped.type} not a supported photo (GIF and SVG are not)`)
  }
  if (skipped.size) {
    const mb = Math.round(DISCUSSION_LIMITS.imageBytes / (1024 * 1024))
    parts.push(`${skipped.size} over ${mb} MB`)
  }
  if (skipped.room) {
    parts.push(
      `${skipped.room} over the limit of ${DISCUSSION_LIMITS.imagesPerSet} per set and ${DISCUSSION_LIMITS.imagesPerPost} per post`
    )
  }
  return parts.length ? `Left out: ${parts.join(', ')}.` : ''
}

// ── links ────────────────────────────────────────────────────────────────────

// `linkPreview` (and its `LinkPreview` shape) live in `link-preview.ts` since the thread's reply
// cards use them too — re-exported so the writer's importers are unchanged.
export { linkPreview, type LinkPreview }

// ── the kind ─────────────────────────────────────────────────────────────────

export interface KindCopy {
  label: string
  hint: string
  titlePlaceholder: string
  postLabel: string
}

export function kindCopy(kind: DiscussionKind): KindCopy {
  return kind === 'question'
    ? {
        label: 'Question',
        hint: 'Ask owners something specific. Answers from people who own the gear are pinned first.',
        titlePlaceholder: 'What do you want to know?',
        postLabel: 'Post question',
      }
    : {
        label: 'Discussion',
        hint: 'Share a setup, a long-term impression or a comparison. Replies are threaded.',
        titlePlaceholder: 'What do you want to talk about?',
        postLabel: 'Post discussion',
      }
}

export const DISCUSSION_KINDS: readonly DiscussionKind[] = ['question', 'discussion']

export function discussionKindParam(raw: unknown): DiscussionKind | null {
  return raw === 'question' || raw === 'discussion' ? raw : null
}

// ── the checklist ────────────────────────────────────────────────────────────

export interface ChecklistItem {
  done: boolean
  label: string
}

export interface Checklist {
  title: string
  items: ChecklistItem[]
}

/** Body text the checklist counts: every text block, trimmed. */
function bodyChars(draft: WriterDraft): number {
  return draft.blocks.reduce((n, b) => n + (b.type === 'text' ? b.body.trim().length : 0), 0)
}

/** A photo that is or will be sent: a failed or waiting tile does not tick the item. */
function hasPhoto(draft: WriterDraft): boolean {
  return draft.blocks.some(
    b =>
      b.type === 'images' && b.images.some(im => im.status === 'done' || im.status === 'uploading')
  )
}

/** The live checklist (design 10b's four rules per kind). Advice only — it never blocks posting. */
export function checklist(draft: WriterDraft): Checklist {
  const t = draft.title.trim()
  const body = bodyChars(draft) >= 80
  const photo = hasPhoto(draft)
  const items: ChecklistItem[] =
    draft.kind === 'question'
      ? [
          { done: t.endsWith('?') && t.length > 12, label: 'Title is a clear question' },
          { done: body, label: 'Say what you use it for and what you tried' },
          { done: photo, label: 'Add a photo if fit, size or a fault matters' },
          {
            done: draft.gear.some(g => g.kind === 'product'),
            label: 'Link the product so its owners see it',
          },
        ]
      : [
          { done: t.length > 12, label: 'Title says what the post is about' },
          { done: body, label: 'Enough text to reply to' },
          { done: photo, label: 'Photos of the gear or setup' },
          { done: draft.gear.length > 0, label: 'Link the gear you mention' },
        ]
  let title = draft.kind === 'question' ? 'Questions that get answered' : 'Posts that get replies'
  if (items.every(i => i.done)) {
    title = 'Ready to post'
  }
  return { title, items }
}

/** A text block with something in it once trimmed — what `toCreateBody` keeps. */
function hasText(draft: WriterDraft): boolean {
  return draft.blocks.some(b => b.type === 'text' && b.body.trim().length > 0)
}

/**
 * Why Post is still off, or null when the draft can be sent: the title rule (design 10b), and —
 * contract §2 "a `discussion` needs ≥ 1 text block; a `question` may be title-only" — a
 * discussion's text. A blank start shows nothing (the title placeholder says enough).
 */
export function postBlocker(draft: WriterDraft): string | null {
  const title = draft.title.trim()
  if (title.length < TITLE_MIN_TO_POST) {
    return title ? `Write a title of at least ${TITLE_MIN_TO_POST} characters to post.` : null
  }
  if (draft.kind === 'discussion' && !hasText(draft)) {
    return 'A discussion needs some text — write at least one paragraph to post.'
  }
  return null
}

export function canPost(draft: WriterDraft): boolean {
  return draft.title.trim().length >= TITLE_MIN_TO_POST && postBlocker(draft) === null
}

// ── the create body ──────────────────────────────────────────────────────────

function toBlock(b: DraftBlock): DiscussionBlock | null {
  switch (b.type) {
    case 'text': {
      const body = b.body.trim()
      return body ? { type: 'text', body } : null
    }
    case 'heading': {
      const text = b.text.trim()
      return text ? { type: 'heading', text } : null
    }
    case 'images': {
      const images: DiscussionImage[] = []
      for (const im of b.images) {
        if (im.status === 'done' && im.media_id && im.url) {
          images.push({ media_id: im.media_id, url: im.url, width: im.width, height: im.height })
        }
      }
      if (!images.length) {
        return null
      }
      const caption = b.caption.trim()
      return caption ? { type: 'images', images, caption } : { type: 'images', images }
    }
    case 'link': {
      const preview = linkPreview(b.url)
      if (!preview) {
        return null
      }
      return preview.title
        ? { type: 'link', url: preview.url, domain: preview.domain, title: preview.title }
        : { type: 'link', url: preview.url, domain: preview.domain }
    }
  }
  return null
}

export function toGearInput(g: DraftGear): DiscussionGearInput {
  return g.kind === 'product'
    ? { kind: 'product', node_id: g.node_id }
    : { kind: 'brand', name: g.name, slug: g.slug }
}

/** What `POST /discussions` receives: empty blocks and unfinished photos dropped, text trimmed. */
export function toCreateBody(draft: WriterDraft): DiscussionCreateBody {
  const body: DiscussionCreateBody = {
    kind: draft.kind,
    title: cleanTitle(draft.title).trim(),
    blocks: draft.blocks.map(toBlock).filter((b): b is DiscussionBlock => b !== null),
    gear: draft.gear.map(toGearInput),
  }
  if (draft.categoryId) {
    body.category_node_id = draft.categoryId
  }
  return body
}

// ── after posting ────────────────────────────────────────────────────────────

/** The only origin an absolute `url` from DealHunt may name (it sends the path since 9867601c;
 *  the absolute form is its commentHub / event spelling). */
const SITE_ORIGIN = 'https://www.soundguys.com'

/**
 * Where the writer goes after a post: DealHunt's `url` — a path, or an absolute
 * `https://www.soundguys.com/...` — when its (normalised) pathname is under
 * `/community/discussions/`; anything else (another host, `//host`, `/\host`, junk) goes to the
 * list. Only the pathname is followed.
 */
export function postedRedirect(url: unknown, trailingSlash: boolean): string {
  const fallback = discussionsHref(trailingSlash)
  if (typeof url !== 'string' || !url || url.includes('\\')) {
    return fallback
  }
  let parsed: URL
  try {
    parsed = new URL(url, SITE_ORIGIN)
  } catch {
    return fallback
  }
  if (parsed.origin !== SITE_ORIGIN || !parsed.pathname.startsWith(`${DISCUSSIONS_PATH}/`)) {
    return fallback
  }
  return parsed.pathname
}

// ── gear search ──────────────────────────────────────────────────────────────

/** A hit the gear panel can link: a product line (a node id) under audio, not linked yet. */
export function linkableHit(hit: DealHuntSuggestHit, linked: readonly DraftGear[]): boolean {
  const nodeId = hitNodeId(hit)
  return (
    nodeId !== null &&
    typeof hit.fullSlug === 'string' &&
    hit.fullSlug.startsWith(`${AUDIO_NODE_SLUG}/`) &&
    !linked.some(g => g.kind === 'product' && g.node_id === nodeId)
  )
}

function hitCategoryLabel(hit: DealHuntSuggestHit): string | null {
  return categoryLeafLabel(
    hit['publicCategoryPath.lvl3']?.[0] ??
      hit['publicCategoryPath.lvl2']?.[0] ??
      hit['publicCategoryPath.lvl1']?.[0]
  )
}

/** "Sony · Headphones" — the brand and the deepest category, whichever are known. */
export function hitSubline(hit: DealHuntSuggestHit): string {
  return [hit.brand, hitCategoryLabel(hit)].filter(Boolean).join(' · ')
}

export function gearFromHit(hit: DealHuntSuggestHit): DraftGear | null {
  const nodeId = hitNodeId(hit)
  if (!nodeId) {
    return null
  }
  return {
    kind: 'product',
    node_id: nodeId,
    name: brandedTitle(hit.brand, hit.canonicalName || hit.title),
    sub: hitSubline(hit),
    image_url: hit.cloudinaryImageUrl || hit.imageUrl || null,
    full_slug: hit.fullSlug,
  }
}

/** The product a `?product=<slug>` link pre-links (`fetchCommunityProduct`). */
export function gearFromCommunityProduct(product: CommunityProduct): DraftGear {
  const brand = product.brand?.name ?? null
  return {
    kind: 'product',
    node_id: product.node_id,
    name: product.name,
    sub: [brand, product.category?.name].filter(Boolean).join(' · '),
    image_url: product.image_url,
    full_slug: product.full_slug ?? product.category?.full_slug ?? null,
  }
}

export interface BrandRow {
  name: string
  slug: string | null
  /** Hits of this brand among the CURRENT results — not the brand's catalogue size (no brand
   *  index exists, contract §10), so the line says "matching". */
  count: number
}

/** Each distinct brand among the hits, in first-seen order (contract: brands come from hits). */
export function brandRowsFromHits(
  hits: readonly DealHuntSuggestHit[],
  linked: readonly DraftGear[] = []
): BrandRow[] {
  const rows = new Map<string, BrandRow>()
  for (const hit of hits) {
    const name = hit.brand?.trim()
    if (!name) {
      continue
    }
    const key = name.toLowerCase()
    const row = rows.get(key)
    if (row) {
      row.count += 1
    } else {
      rows.set(key, { name, slug: hit.brandSlug ?? null, count: 1 })
    }
  }
  const taken = new Set(linked.filter(g => g.kind === 'brand').map(g => g.name.toLowerCase()))
  return Array.from(rows.values()).filter(r => !taken.has(r.name.toLowerCase()))
}

export function brandRowLine(row: BrandRow): string {
  return `Brand · ${row.count} matching product${row.count === 1 ? '' : 's'}`
}

export function gearFromBrand(row: BrandRow): DraftGear {
  return { kind: 'brand', name: row.name, slug: row.slug, sub: 'brand' }
}

// ── category ─────────────────────────────────────────────────────────────────

const AUDIO_DEPTH = AUDIO_NODE_SLUG.split('/').length

/** The pills: the top-level categories under audio, in DealHunt's order. */
export function topCategories(
  categories: readonly CommunityCategoryNode[]
): CommunityCategoryNode[] {
  return categories.filter(
    c =>
      c.full_slug.startsWith(`${AUDIO_NODE_SLUG}/`) &&
      c.full_slug.split('/').length === AUDIO_DEPTH + 1
  )
}

/** The category card's two rows: the top-level pills, and — once one is picked — its children. */
export interface WriterCategoryRows {
  top: CommunityCategoryNode[]
  /** The lit top-level pill: the node picked, or the top-level node above it. */
  parentId: string | null
  /** The lit parent's children ([] with no parent, or a parent without children). */
  children: CommunityCategoryNode[]
  /** The lit child: the node picked, or the child above it (a deeper preset). */
  childId: string | null
}

function within(node: CommunityCategoryNode, ancestor: CommunityCategoryNode): boolean {
  return (
    node.full_slug === ancestor.full_slug || node.full_slug.startsWith(`${ancestor.full_slug}/`)
  )
}

/** The rows for the node picked (`categoryId`, any depth), from DealHunt's full list. */
export function writerCategoryRows(
  categories: readonly CommunityCategoryNode[],
  selectedId: string | null
): WriterCategoryRows {
  const top = topCategories(categories)
  const selected = selectedId ? categories.find(c => c.id === selectedId) ?? null : null
  const parent = selected ? top.find(t => within(selected, t)) ?? null : null
  const children = parent ? categoryChildren([...categories], parent.full_slug) : []
  const child = selected ? children.find(c => within(selected, c)) ?? null : null
  return { top, parentId: parent?.id ?? null, children, childId: child?.id ?? null }
}

/**
 * The node a press picks. Pressing an unlit pill picks it; pressing the lit child goes back to its
 * parent (the second row stays); pressing the lit parent clears the category — as before
 * subcategories, pressing the selected one clears it.
 */
export function pressCategory(
  rows: Pick<WriterCategoryRows, 'parentId' | 'childId'>,
  pressed: CommunityCategoryNode,
  level: 'top' | 'child'
): string | null {
  if (level === 'child') {
    return pressed.id === rows.childId ? rows.parentId : pressed.id
  }
  return pressed.id === rows.parentId ? null : pressed.id
}

/** The pill the first linked product sits under, or null. */
export function categoryFromGear(
  gear: readonly DraftGear[],
  pills: readonly CommunityCategoryNode[]
): string | null {
  const product = gear.find(g => g.kind === 'product')
  const slug = product?.kind === 'product' ? product.full_slug : null
  if (!slug) {
    return null
  }
  const pill = pills.find(c => slug === c.full_slug || slug.startsWith(`${c.full_slug}/`))
  return pill?.id ?? null
}

// ── presets (`?kind=`, `?product=`, `?category=`, `?title=`) ─────────────────

export interface WriterPreset {
  kind: DiscussionKind | null
  product: DraftGear | null
  /** A community category PATH (`earbuds`, `earbuds/wireless-earbuds` — the Discussions tab's
   *  CTAs under a picked category), validated by the server (`categoryPathQueryParam`) and resolved
   *  here against DealHunt's list (`findCategory`); an unknown path presets nothing. A path, not a
   *  slug: child slugs repeat under different parents. Absent from older callers. */
  category?: string | null
  /** An opener (the Opener card) — the title, when the draft has none yet. */
  title?: string | null
}

/** `?title=` as the writer takes it: one line, DealHunt's length, non-blank — or null. */
export function discussionTitleParam(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null
  }
  const title = cleanTitle(raw).trim()
  return title ? title : null
}

export function presetKey(preset: WriterPreset): string | null {
  const category = preset.category ?? ''
  const title = preset.title ?? ''
  if (!preset.kind && !preset.product && !category && !title) {
    return null
  }
  const product = preset.product?.kind === 'product' ? preset.product.node_id : ''
  // `kind|product` as stored drafts carry it (f3c43e6b); the two newer parts only when set, so a
  // draft stored before them is not re-preset on its next visit.
  const base = `${preset.kind ?? ''}|${product}`
  return category || title ? `${base}|${category}|${title}` : base
}

/**
 * Apply the link's preset once per draft: a reload of the same URL keeps what the reader changed
 * since (they may have unlinked the product), a link from another product page adds that one, a
 * category path picks its node, an opener fills a blank title (never over the reader's own words).
 * `categories` is DealHunt's full list (the writer page's `/categories` read).
 */
export function applyPreset(
  draft: WriterDraft,
  preset: WriterPreset,
  categories: readonly CommunityCategoryNode[]
): WriterDraft {
  // A linked product picks its TOP-level pill (`categoryFromGear`, unchanged); a `?category=`
  // path picks its node at any depth.
  const pills = topCategories(categories)
  const key = presetKey(preset)
  if (!key || draft.presetKey === key) {
    return draft
  }
  let next: WriterDraft = { ...draft, presetKey: key }
  if (preset.kind) {
    next = writerReducer(next, { type: 'setKind', kind: preset.kind })
  }
  if (preset.product) {
    next = writerReducer(next, {
      type: 'addGear',
      gear: preset.product,
      categoryId: categoryFromGear([preset.product], pills),
    })
  }
  const node = preset.category ? findCategory([...categories], preset.category) : null
  if (node) {
    next = writerReducer(next, { type: 'setCategory', id: node.id })
  }
  if (preset.title && !next.title.trim()) {
    next = writerReducer(next, { type: 'setTitle', title: preset.title })
  }
  return next
}

// ── local draft storage ──────────────────────────────────────────────────────

export const DRAFT_STORAGE_KEY = 'sg:discussion-draft:v1'

function str(v: unknown, max: number): string {
  return typeof v === 'string' ? v.slice(0, max) : ''
}

/** The one tile line a stored draft may carry back (`imageStatusMarker('hidden')`). */
const HIDDEN_MARKER_LABEL = imageStatusMarker('hidden')?.label ?? null

function readImage(raw: unknown, now: number): DraftImage | null {
  const im = raw as Partial<DraftImage> | null
  if (
    !im ||
    im.status !== 'done' ||
    typeof im.media_id !== 'string' ||
    typeof im.url !== 'string' ||
    typeof im.uploaded_at !== 'number' ||
    now - im.uploaded_at > PHOTO_DRAFT_TTL_MS
  ) {
    return null
  }
  if (!/^https:\/\//.test(im.url)) {
    return null
  }
  return {
    key: typeof im.key === 'string' ? im.key : im.media_id,
    status: 'done',
    progress: 1,
    media_id: im.media_id,
    url: im.url,
    width: typeof im.width === 'number' ? im.width : 0,
    height: typeof im.height === 'number' ? im.height : 0,
    error: null,
    uploaded_at: im.uploaded_at,
    // Only a marker this build still draws ("Not approved"): a draft stored before the photo
    // gate went off (contract-review-photos §5) may hold "Shown after a quick review".
    pending_line: im.pending_line === HIDDEN_MARKER_LABEL ? HIDDEN_MARKER_LABEL : null,
  }
}

function readBlock(raw: unknown, id: number, now: number): DraftBlock | null {
  const b = raw as Record<string, unknown> | null
  switch (b?.type) {
    case 'text':
      return { id, type: 'text', body: str(b.body, DISCUSSION_LIMITS.textChars) }
    case 'heading':
      return { id, type: 'heading', text: str(b.text, DISCUSSION_LIMITS.headingChars) }
    case 'images': {
      const images = (Array.isArray(b.images) ? b.images : [])
        .map(im => readImage(im, now))
        .filter((im): im is DraftImage => im !== null)
        .slice(0, DISCUSSION_LIMITS.imagesPerSet)
      return { id, type: 'images', images, caption: str(b.caption, DISCUSSION_LIMITS.captionChars) }
    }
    case 'link':
      return { id, type: 'link', url: str(b.url, DISCUSSION_LIMITS.urlChars) }
  }
  return null
}

function readGear(raw: unknown): DraftGear | null {
  const g = raw as Record<string, unknown> | null
  if (g?.kind === 'product' && typeof g.node_id === 'string' && typeof g.name === 'string') {
    return {
      kind: 'product',
      node_id: g.node_id,
      name: g.name,
      sub: str(g.sub, 200),
      image_url: typeof g.image_url === 'string' ? g.image_url : null,
      full_slug: typeof g.full_slug === 'string' ? g.full_slug : null,
    }
  }
  if (g?.kind === 'brand' && typeof g.name === 'string' && g.name) {
    return {
      kind: 'brand',
      name: g.name,
      slug: typeof g.slug === 'string' ? g.slug : null,
      sub: 'brand',
    }
  }
  return null
}

/** A stored draft, re-validated field by field (an older or hand-edited value is not trusted). */
export function parseDraft(raw: string | null, now = Date.now()): WriterDraft | null {
  if (!raw) {
    return null
  }
  let value: Record<string, unknown>
  try {
    value = JSON.parse(raw) as Record<string, unknown>
  } catch {
    return null
  }
  if (!value || typeof value !== 'object') {
    return null
  }
  const kind = discussionKindParam(value.kind) ?? 'question'
  // A time from this browser's clock: a value from the future (beyond a minute's skew) is junk.
  const savedAt =
    typeof value.savedAt === 'number' &&
    Number.isFinite(value.savedAt) &&
    value.savedAt > 0 &&
    value.savedAt <= now + 60_000
      ? value.savedAt
      : null
  // Ids are numbered after the unknown blocks are dropped: `nextId` must stay above every id.
  const blocks = (Array.isArray(value.blocks) ? value.blocks : [])
    .slice(0, DISCUSSION_LIMITS.blocks)
    .map(b => readBlock(b, 0, now))
    .filter((b): b is DraftBlock => b !== null)
    .map((b, i) => ({ ...b, id: i + 1 }))
  // Over-cap photos or text from a tampered value: trim the post totals in order (`textRoom`
  // assumes the text total — bodies + headings + captions — holds; over it, a keystroke would
  // empty the block).
  // A repeated photo (same tile key or media id) is kept once: the key names a tile, and DealHunt
  // refuses a post that carries one media id twice.
  let left: number = DISCUSSION_LIMITS.imagesPerPost
  let textLeft: number = DISCUSSION_LIMITS.totalTextChars
  const seenPhotos = new Set<string>()
  for (const b of blocks) {
    if (b.type === 'images') {
      b.images = b.images
        .filter(im => {
          const repeat = seenPhotos.has(`k:${im.key}`) || seenPhotos.has(`m:${im.media_id}`)
          seenPhotos.add(`k:${im.key}`).add(`m:${im.media_id}`)
          return !repeat
        })
        .slice(0, left)
      left -= b.images.length
      b.caption = b.caption.slice(0, textLeft)
      textLeft -= b.caption.length
    } else if (b.type === 'text') {
      b.body = b.body.slice(0, textLeft)
      textLeft -= b.body.length
    } else if (b.type === 'heading') {
      b.text = b.text.slice(0, textLeft)
      textLeft -= b.text.length
    }
  }
  // Linked once each, as `addGear` keeps them (a repeat would double a chip and its React key).
  const linked = new Set<string>()
  const gear = (Array.isArray(value.gear) ? value.gear : [])
    .map(readGear)
    .filter((g): g is DraftGear => {
      if (!g || linked.has(gearKey(g))) {
        return false
      }
      linked.add(gearKey(g))
      return true
    })
    .slice(0, DISCUSSION_LIMITS.gear)
  return {
    kind,
    title: cleanTitle(str(value.title, DISCUSSION_LIMITS.titleChars)),
    blocks: blocks.length ? blocks : [{ id: 1, type: 'text', body: '' }],
    gear,
    categoryId: typeof value.categoryId === 'string' ? value.categoryId : null,
    categoryTouched: value.categoryTouched === true,
    nextId: Math.max(blocks.length, 1) + 1,
    presetKey: typeof value.presetKey === 'string' ? value.presetKey : null,
    ...(savedAt !== null ? { savedAt } : {}),
  }
}

function storedBlock(b: DraftBlock): DraftBlock {
  return b.type === 'images' ? { ...b, images: b.images.filter(im => im.status === 'done') } : b
}

/** The stored form: unfinished photos are left out (a local file cannot be stored). `savedAt` is
 *  this save's time — never one the draft carried from an earlier load. */
export function serializeDraft(draft: WriterDraft, savedAt: number | null = null): string {
  return JSON.stringify({
    ...draft,
    blocks: draft.blocks.map(storedBlock),
    savedAt: savedAt ?? undefined,
  })
}

/** Nothing the reader would miss: the start state of a new post. */
export function isBlankDraft(draft: WriterDraft): boolean {
  return (
    !draft.title.trim() &&
    !draft.gear.length &&
    draft.blocks.every(
      b =>
        (b.type === 'text' && !b.body.trim()) ||
        (b.type === 'heading' && !b.text.trim()) ||
        (b.type === 'link' && !b.url.trim()) ||
        (b.type === 'images' && !b.images.length && !b.caption.trim())
    )
  )
}

export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function storage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

/** `localStorage` can be missing or throw (private mode, blocked site data, quota): every call
 *  answers instead — `save` false means "not saved", and the header says so. */
export const draftStorage = {
  load(store: StorageLike | null = storage()): WriterDraft | null {
    try {
      return parseDraft(store?.getItem(DRAFT_STORAGE_KEY) ?? null)
    } catch {
      return null
    }
  },
  save(draft: WriterDraft, store: StorageLike | null = storage(), now = Date.now()): boolean {
    if (!store) {
      return false
    }
    try {
      store.setItem(DRAFT_STORAGE_KEY, serializeDraft(draft, now))
      return true
    } catch {
      return false
    }
  },
  clear(store: StorageLike | null = storage()): void {
    try {
      store?.removeItem(DRAFT_STORAGE_KEY)
    } catch {
      // Nothing to do: the draft stays, and the next post replaces it.
    }
  },
}

// ── save feedback ────────────────────────────────────────────────────────────

/** How long Save draft reads "Saved" after a press. */
export const SAVED_FLASH_MS = 2000

/** When the header's status says the draft was saved: the reader's LOCAL time, 24 h `HH:MM`.
 *  Browser-only — the server never knows when (or whether) this browser saved. The line around
 *  it ("Draft saved on this device · HH:MM · Find it in your profile") is `writerSavedStatus`
 *  (`draft-card.ts`). */
export function draftSavedClock(savedAt: number): string {
  const at = new Date(savedAt)
  return `${twoDigits(at.getHours())}:${twoDigits(at.getMinutes())}`
}

// ── the restored draft ───────────────────────────────────────────────────────

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** "saved 3 h ago": how old a stored draft is, coarse on purpose (a clock skew reads "just now"). */
export function savedAgo(savedAt: number, now: number): string {
  const age = now - savedAt
  if (age < MINUTE_MS) {
    return 'saved just now'
  }
  if (age < HOUR_MS) {
    return `saved ${Math.floor(age / MINUTE_MS)} min ago`
  }
  if (age < DAY_MS) {
    return `saved ${Math.floor(age / HOUR_MS)} h ago`
  }
  const days = Math.floor(age / DAY_MS)
  return `saved ${days} ${days === 1 ? 'day' : 'days'} ago`
}

/** The bar over a draft loaded from this browser; an older stored draft has no time to give. */
export function restoredDraftLine(savedAt: number | null, now: number): string {
  const line = 'Draft restored from this device'
  return savedAt === null ? line : `${line} · ${savedAgo(savedAt, now)}`
}

/** A new post with the link's preset applied: the server's start state, and what Discard draft
 *  leaves — so "Ask owners" from a product page lands on THAT product even after an old stored
 *  draft had absorbed the preset. */
export function freshDraft(
  preset: WriterPreset,
  categories: readonly CommunityCategoryNode[]
): WriterDraft {
  return applyPreset(emptyDraft(preset.kind ?? 'question'), preset, categories)
}

// ── "Continue your draft" (the Discussions list) ─────────────────────────────

export interface DraftSummary {
  kind: DiscussionKind
  /** The draft's title, trimmed; null while it has none. */
  title: string | null
  savedAt: number | null
}

/** What the list says about the draft this browser holds; null when there is none worth naming. */
export function draftSummary(draft: WriterDraft | null): DraftSummary | null {
  if (!draft || isBlankDraft(draft)) {
    return null
  }
  const title = cleanTitle(draft.title).trim()
  return { kind: draft.kind, title: title || null, savedAt: draft.savedAt ?? null }
}

/** The rail's line before "Continue writing". */
export function unfinishedDraftLine(summary: DraftSummary): string {
  return `You have an unfinished ${summary.kind === 'question' ? 'question' : 'discussion'}`
}

function session(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

/**
 * A one-time note handed from one page to the next through sessionStorage: written just before
 * the navigation, taken (read AND removed) once, after mount, by the page it lands on — never in
 * that page's HTML, which is shared-cached. `parse` turns what was stored into the note (null =
 * none). Storage failures are silent: the note is a courtesy, never the outcome.
 */
export interface OneTimeNotice<T, W> {
  write(value: W, store?: StorageLike | null): void
  take(store?: StorageLike | null): T | null
}

export function oneTimeNotice<T, W>(
  key: string,
  parse: (raw: string) => T | null
): OneTimeNotice<T, W> {
  return {
    write(value: W, store: StorageLike | null = session()): void {
      try {
        store?.setItem(key, JSON.stringify(value))
      } catch {
        // Private mode / quota: no note.
      }
    },
    take(store: StorageLike | null = session()): T | null {
      try {
        const raw = store?.getItem(key) ?? null
        if (raw === null) {
          return null
        }
        store?.removeItem(key)
        return parse(raw)
      } catch {
        return null
      }
    },
  }
}

function parsedObject(raw: string): Record<string, unknown> | null {
  try {
    const value = JSON.parse(raw) as unknown
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
  } catch {
    return null
  }
}

// ── after a delete: the Discussions list's one-time note ──────────────────────

/** `sessionStorage`: from the thread's Delete to the Discussions list it lands on. */
export const DELETED_NOTICE_KEY = 'sg:discussion-deleted:v1'

/** What the list knows about the post just deleted. Every field may be unknown (an older tab
 *  wrote `'1'`, or a junk value): the note then falls back to its general wording. */
export interface DeletedNotice {
  /** Lowercase — the list hides this card for the visit (its HTML is shared-cached and can still
   *  hold it). */
  id: string | null
  kind: DiscussionKind | null
  /** DealHunt's `answer_count` as the thread showed it (cached, can lag); null = not sent. */
  answers: number | null
}

/** Any stored value is a delete that happened; what it says is read field by field. */
export function deletedNotice(raw: string): DeletedNotice {
  const value = parsedObject(raw)
  const id = typeof value?.id === 'string' && value.id ? value.id.toLowerCase() : null
  const kind = value?.kind === 'question' || value?.kind === 'discussion' ? value.kind : null
  const n = value?.answers
  const answers = typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.floor(n) : null
  return { id, kind, answers }
}

/**
 * The note's two parts: what happened, then what it means. The answer count comes from the
 * shared-cached thread, so — like the confirmation (`deleteDiscussionLine`) — a 0 or an unsent
 * count never claims "no answers". The last sentence is honest about caches: the thread and the
 * lists are shared-cached for minutes (s-maxage=300 here, 60 s at DealHunt), so a page someone
 * else loads can still show the post for a short while.
 */
export function deletedNoticeLines(notice: DeletedNotice): { head: string; detail: string } {
  const head = notice.kind === 'question' ? 'Your question was deleted.' : 'Your post was deleted.'
  const n = notice.answers ?? 0
  let gone = 'It and any answers are no longer visible to anyone.'
  if (n === 1) {
    gone = 'It and its answer are no longer visible to anyone.'
  } else if (n > 1) {
    gone = `It and its ${n.toLocaleString('en-US')} answers are no longer visible to anyone.`
  }
  return {
    head,
    detail: `${gone} Some pages can take a few minutes to catch up.`,
  }
}

/** Written by the author's Delete (`AuthorMenu`), taken once by the Discussions list. */
export const deletedNoticeStorage = oneTimeNotice<
  DeletedNotice,
  { id: string; kind: DiscussionKind; answers: unknown }
>(DELETED_NOTICE_KEY, deletedNotice)

// ── after an edit: the thread's one-time note ─────────────────────────────────

/** `sessionStorage`: from the edit page's Save to the thread it lands on. */
export const SAVED_NOTICE_KEY = 'sg:discussion-saved:v1'

/**
 * The note after a save. The author sees their edit at once (their own projection drives the
 * thread's title, marker, category and blocks); everyone else reads the shared-cached thread,
 * which turns over within minutes (s-maxage=300 here, 60 s at DealHunt) — the second sentence.
 */
export const SAVED_NOTICE_LINE =
  'Your changes are saved. Other readers may see the earlier version for a few minutes.'

/** The saved post's id, lowercase; null for anything else. */
function savedNotice(raw: string): string | null {
  const id = parsedObject(raw)?.id
  return typeof id === 'string' && id ? id.toLowerCase() : null
}

/** Written by the edit page's Save, taken once by the thread (shown only on the post saved). */
export const savedNoticeStorage = oneTimeNotice<string, { id: string }>(
  SAVED_NOTICE_KEY,
  savedNotice
)

// ── editing a posted discussion (`/community/discussions/edit/<id>`) ─────────

/** A posted photo as an already-uploaded tile: its media id is the key (unique per post), and no
 *  `uploaded_at` — it is attached, so DealHunt's 24 h GC of unattached media never applies and
 *  `expiredPhotos` skips it. A hidden photo carries the thread page's marker. */
function tileFromPosted(raw: unknown): DraftImage | null {
  const im = raw as Partial<DiscussionImage> | null
  if (!im || typeof im.media_id !== 'string' || !im.media_id || typeof im.url !== 'string') {
    return null
  }
  const status = im.status === 'pending' || im.status === 'hidden' ? im.status : undefined
  const tile: DraftImage = {
    key: im.media_id,
    status: 'done',
    progress: 1,
    media_id: im.media_id,
    url: im.url,
    width: typeof im.width === 'number' ? im.width : 0,
    height: typeof im.height === 'number' ? im.height : 0,
    error: null,
    uploaded_at: null,
    pending_line: imageStatusMarker(status)?.label ?? null,
    preview_url: imageSource(im, 'card', 'card')?.src ?? null,
  }
  if (status) {
    tile.posted_status = status
  }
  return tile
}

function blockFromPosted(raw: unknown, id: number): DraftBlock | null {
  const b = raw as Record<string, unknown> | null
  switch (b?.type) {
    case 'text':
      return { id, type: 'text', body: str(b.body, DISCUSSION_LIMITS.textChars) }
    case 'heading':
      return { id, type: 'heading', text: str(b.text, DISCUSSION_LIMITS.headingChars) }
    case 'images': {
      const images = (Array.isArray(b.images) ? b.images : [])
        .map(tileFromPosted)
        .filter((im): im is DraftImage => im !== null)
        .slice(0, DISCUSSION_LIMITS.imagesPerSet)
      return images.length
        ? { id, type: 'images', images, caption: str(b.caption, DISCUSSION_LIMITS.captionChars) }
        : null
    }
    case 'link':
      return typeof b.url === 'string' && b.url
        ? { id, type: 'link', url: str(b.url, DISCUSSION_LIMITS.urlChars) }
        : null
  }
  return null
}

function gearFromPosted(raw: unknown): DraftGear | null {
  const g = raw as Record<string, unknown> | null
  if (g?.kind === 'product' && typeof g.node_id === 'string' && typeof g.name === 'string') {
    const brand = (g.brand as { name?: unknown } | undefined)?.name
    return {
      kind: 'product',
      node_id: g.node_id,
      name: g.name,
      sub: typeof brand === 'string' ? brand : '',
      image_url: typeof g.image_url === 'string' ? g.image_url : null,
      // The chip carries no taxonomy path: an added product may still pick the category when
      // the post has none (`addGear`), a posted one never moves it.
      full_slug: null,
    }
  }
  if (g?.kind === 'brand' && typeof g.name === 'string' && g.name) {
    return {
      kind: 'brand',
      name: g.name,
      slug: typeof g.slug === 'string' ? g.slug : null,
      sub: 'brand',
    }
  }
  return null
}

/**
 * The author's posted discussion (`GET …/discussions/:id/mine` — every photo they attached, held
 * ones included, which the public read leaves out) as the writer's draft: title, kind, blocks,
 * photo tiles already uploaded, linked gear in DealHunt's order, category. A title-only question
 * gets one empty text block to type into (dropped again on save if left empty). Tolerant of any
 * shape, like every keyed read.
 */
export function draftFromDiscussion(thread: DiscussionThread): WriterDraft {
  const blocks: DraftBlock[] = []
  let nextId = 1
  for (const raw of Array.isArray(thread.blocks) ? thread.blocks : []) {
    if (blocks.length >= DISCUSSION_LIMITS.blocks) {
      break
    }
    const block = blockFromPosted(raw, nextId)
    if (block) {
      blocks.push(block)
      nextId += 1
    }
  }
  if (!blocks.length) {
    blocks.push({ id: nextId, type: 'text', body: '' })
    nextId += 1
  }
  const gear: DraftGear[] = []
  const seen = new Set<string>()
  for (const raw of Array.isArray(thread.gear) ? thread.gear : []) {
    const g = gearFromPosted(raw)
    if (g && !seen.has(gearKey(g)) && gear.length < DISCUSSION_LIMITS.gear) {
      seen.add(gearKey(g))
      gear.push(g)
    }
  }
  const categoryId = typeof thread.category?.id === 'string' ? thread.category.id : null
  return {
    kind: thread.kind === 'discussion' ? 'discussion' : 'question',
    title: cleanTitle(typeof thread.title === 'string' ? thread.title : ''),
    blocks,
    gear,
    categoryId,
    categoryTouched: false,
    nextId,
    presetKey: null,
  }
}

/**
 * Linked gear can no longer change: DealHunt answers 409 `gear_locked` once `answer_count > 0`
 * and the gear differs (contract §4). Locked up front so the author is not refused after
 * editing; a count that is not a number does not lock (the 409 still guards).
 */
export function gearLockedFor(thread: Pick<DiscussionThread, 'answer_count'>): boolean {
  return typeof thread.answer_count === 'number' && thread.answer_count > 0
}

/** The line in the locked gear card. */
export const GEAR_LOCKED_LINE =
  'Linked gear is fixed once someone has answered, so their answers stay about the same products.'

/** Under the Save button in the edit view: why the kind cannot be switched. */
export const KIND_FIXED_LINE = "The kind of post can't change once it's posted."

/**
 * The PATCH body: only the fields that differ from the post as it loaded. Both sides go through
 * `toCreateBody`, so trimming and dropped empty blocks never read as a change. `gear` is never
 * sent while locked — DealHunt compares it in order (brand slug included), so even a reorder of
 * the same items would be refused. `category_node_id: null` clears the category. `{}` = nothing
 * to save.
 */
export function toPatchBody(
  initial: WriterDraft,
  current: WriterDraft,
  opts: { gearLocked: boolean }
): DiscussionPatchBody {
  const before = toCreateBody(initial)
  const after = toCreateBody(current)
  const body: DiscussionPatchBody = {}
  if (after.title !== before.title) {
    body.title = after.title
  }
  if (JSON.stringify(after.blocks) !== JSON.stringify(before.blocks)) {
    body.blocks = after.blocks
  }
  if (!opts.gearLocked && JSON.stringify(after.gear) !== JSON.stringify(before.gear)) {
    body.gear = after.gear
  }
  if ((current.categoryId ?? null) !== (initial.categoryId ?? null)) {
    body.category_node_id = current.categoryId ?? null
  }
  return body
}

export function isEmptyPatch(body: DiscussionPatchBody): boolean {
  return Object.keys(body).length === 0
}

// ── leaving the edit page with unsaved changes ────────────────────────────────

/** The edit has something a save would send, or a photo still uploading (which `toPatchBody`
 *  cannot see yet) — the leave guard is armed exactly then. */
export function editHasChanges(patch: DiscussionPatchBody | null, draft: WriterDraft): boolean {
  return patch !== null && (!isEmptyPatch(patch) || uploadsPending(draft))
}

/** What the leave guard reads off a click (a `MouseEvent`'s fields). */
export interface LeaveClick {
  defaultPrevented: boolean
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
}

/** What it reads off the link clicked. */
export interface LeaveLink {
  href: string
  target: string
  download: boolean
}

/**
 * Where a click on a link would take the reader AWAY from the edit page — the URL the guard
 * holds while it asks "Discard your changes?" — or null when the click is not a leave: already
 * handled, not a plain left click (a modifier or middle click opens another tab and this one
 * stays), a link to another window or a download, no usable href, or a jump within this page
 * (same path and query, a `#hash` only).
 */
export function leaveTarget(click: LeaveClick, link: LeaveLink, here: string): string | null {
  if (
    click.defaultPrevented ||
    click.button !== 0 ||
    click.metaKey ||
    click.ctrlKey ||
    click.shiftKey ||
    click.altKey ||
    (link.target && link.target !== '_self') ||
    link.download ||
    !link.href
  ) {
    return null
  }
  let to: URL
  let from: URL
  try {
    from = new URL(here)
    to = new URL(link.href, from)
  } catch {
    return null
  }
  if (to.protocol !== 'http:' && to.protocol !== 'https:') {
    return null
  }
  if (to.origin === from.origin && to.pathname === from.pathname && to.search === from.search) {
    return null
  }
  return to.href
}
